import { Flavor, layers, namedFlavor } from "@protomaps/basemaps";
import type { LayerSpecification, StyleSpecification } from "maplibre-gl";

export const MAPTILER_STYLE_URL =
    "https://api.maptiler.com/maps/8d80423a-4979-4e17-a487-ce521f75ff8e/style.json?key=Xs35dG5Gh6ueSw4mF06I";

export const MAP_CDN_BASE = "https://d1alzjajiceuze.cloudfront.net";
export const BASEMAP_MANIFEST_URL = `${MAP_CDN_BASE}/basemap-tiles/manifest.json`;
const BASEMAP_ASSETS_BASE = `${MAP_CDN_BASE}/basemap-tiles/assets`;

const seniorPlaceFlavor: Flavor = {
    ...namedFlavor("light"),
    background: "hsl(48, 0%, 92%)",
    earth: "hsl(48, 0%, 92%)",
    park_a: "hsla(134, 36%, 73%, 0.5)",
    park_b: "hsla(134, 36%, 73%, 0.5)",
    hospital: "hsl(195, 0%, 92%)",
    industrial: "hsl(40, 67%, 90%)",
    school: "hsl(195, 0%, 92%)",
    wood_a: "hsla(130, 43%, 66%, 0.5)",
    wood_b: "hsla(130, 43%, 66%, 0.5)",
    pedestrian: "hsl(44, 0%, 97%)",
    scrub_a: "hsla(134, 36%, 78%, 0.5)",
    scrub_b: "hsla(134, 36%, 78%, 0.5)",
    glacier: "hsl(31, 0%, 86%)",
    sand: "hsla(83, 84%, 77%, 0.5)",
    beach: "hsla(83, 84%, 77%, 0.5)",
    aerodrome: "hsl(1, 0%, 91%)",
    runway: "hsl(5, 0%, 98%)",
    water: "hsl(215, 94%, 80%)",
    zoo: "hsl(95, 0%, 92%)",
    military: "hsl(41, 5%, 88%)",
    tunnel_other_casing: "hsl(41, 5%, 84%)",
    tunnel_minor_casing: "hsl(41, 5%, 84%)",
    tunnel_link_casing: "hsl(33, 72%, 74%)",
    tunnel_major_casing: "hsl(41, 5%, 84%)",
    tunnel_highway_casing: "hsl(33, 72%, 74%)",
    tunnel_other: "hsl(5, 0%, 97%)",
    tunnel_minor: "hsl(5, 0%, 97%)",
    tunnel_link: "hsl(53, 100%, 90%)",
    tunnel_major: "hsl(5, 0%, 97%)",
    tunnel_highway: "hsl(40, 100%, 85%)",
    pier: "hsl(43, 0%, 91%)",
    buildings: "hsla(30, 6%, 73%, 0.3)",
    minor_service_casing: "hsl(41, 5%, 84%)",
    minor_casing: "hsl(41, 5%, 84%)",
    link_casing: "hsl(33, 72%, 74%)",
    major_casing_late: "hsl(41, 5%, 84%)",
    highway_casing_late: "hsl(33, 72%, 74%)",
    other: "hsl(44, 0%, 97%)",
    minor_service: "hsl(5, 0%, 97%)",
    minor_a: "hsl(5, 0%, 100%)",
    minor_b: "hsl(5, 0%, 100%)",
    link: "hsl(53, 100%, 86%)",
    major_casing_early: "hsl(41, 5%, 84%)",
    major: "hsl(5, 0%, 100%)",
    highway_casing_early: "hsl(33, 72%, 74%)",
    highway: "hsl(40, 100%, 80%)",
    railway: "hsl(5, 0%, 77%)",
    boundaries: "hsl(0, 0%, 54%)",
    bridges_other_casing: "hsl(41, 5%, 84%)",
    bridges_minor_casing: "hsl(41, 5%, 84%)",
    bridges_link_casing: "hsl(33, 72%, 74%)",
    bridges_major_casing: "hsl(41, 5%, 84%)",
    bridges_highway_casing: "hsl(33, 72%, 74%)",
    bridges_other: "hsl(44, 0%, 97%)",
    bridges_minor: "hsl(5, 0%, 100%)",
    bridges_link: "hsl(53, 100%, 86%)",
    bridges_major: "hsl(5, 0%, 100%)",
    bridges_highway: "hsl(40, 100%, 80%)",
    roads_label_minor: "hsl(19, 0%, 16%)",
    roads_label_minor_halo: "hsl(0, 0%, 100%)",
    roads_label_major: "hsl(19, 0%, 16%)",
    roads_label_major_halo: "hsl(0, 0%, 100%)",
    ocean_label: "hsl(214, 79%, 51%)",
    subplace_label: "hsl(0, 0%, 25%)",
    subplace_label_halo: "hsl(0, 0%, 100%)",
    city_label: "hsl(0, 0%, 13%)",
    city_label_halo: "hsl(0, 0%, 100%)",
    state_label: "hsl(48, 4%, 44%)",
    state_label_halo: "hsla(0, 0%, 100%, 0.75)",
    country_label: "hsl(0, 0%, 20%)",
    address_label: "hsl(26, 10%, 44%)",
    address_label_halo: "hsl(21, 64%, 96%)",
    landcover: {
        grassland: "hsla(134, 36%, 73%, 0.5)",
        barren: "hsl(48, 0%, 90%)",
        urban_area: "hsl(45, 0%, 87%)",
        farmland: "hsla(134, 30%, 80%, 0.5)",
        glacier: "hsl(31, 0%, 92%)",
        scrub: "hsla(134, 36%, 78%, 0.5)",
        forest: "hsla(130, 43%, 66%, 0.5)",
    },
};

export interface BasemapManifest {
    tiles: string;
}

export const parseBasemapManifest = (value: unknown): BasemapManifest => {
    if (typeof value === "object" && value !== null) {
        const tiles = (value as { tiles?: unknown }).tiles;
        if (typeof tiles === "string" && tiles.startsWith("/")) {
            return { tiles };
        }
    }
    throw new Error("Invalid basemap manifest");
};

const emphasizeCityLabels = (mapLayers: LayerSpecification[]): LayerSpecification[] =>
    mapLayers.map(layer =>
        layer.id === "places_locality" && layer.type === "symbol"
            ? {
                  ...layer,
                  layout: {
                      ...layer.layout,
                      "text-font": ["Noto Sans Medium"],
                      "text-letter-spacing": -0.02,
                  },
              }
            : layer,
    );

export const buildSelfHostedMapStyle = (tilePath: string): StyleSpecification => ({
    version: 8,
    glyphs: `${BASEMAP_ASSETS_BASE}/fonts/{fontstack}/{range}.pbf`,
    sprite: `${BASEMAP_ASSETS_BASE}/sprites/v4/light`,
    sources: {
        protomaps: {
            type: "vector",
            url: `pmtiles://${MAP_CDN_BASE}${tilePath}`,
            attribution:
                '&copy; <a href="https://openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> contributors',
        },
    },
    layers: emphasizeCityLabels(layers("protomaps", seniorPlaceFlavor, { lang: "en" })),
});
